import React from "react";
import { Link } from "react-router-dom";
import FormBar from "../FormBar";
import { LeaderboardColumn, LeaderboardCtx, LeagueMode, Member } from "./types";
import { memberHref } from "./links";

type TFn = (key: string, opts?: Record<string, unknown>) => string;

// ─── Shared cell helpers ──────────────────────────────────────────────────────

const RANK_BADGE_COLORS: Record<number, string> = {
  1: "oklch(0.72 0.15 85)",  // gold
  2: "oklch(0.58 0.14 255)", // blue
  3: "oklch(0.65 0.18 35)",  // coral
};

function rankBadgeStyle(rankNum: number): { background: string; border: string; color: string } {
  const fill = RANK_BADGE_COLORS[rankNum];
  if (fill) return { background: fill, border: fill, color: "white" };
  return { background: "transparent", border: "var(--color-border)", color: "var(--color-text)" };
}

function deltaCell(m: Member, isNr: boolean, ctx: LeaderboardCtx): React.ReactNode {
  if (!ctx.isStarted || isNr || m.delta === 0) {
    return <span style={{ fontSize: "0.7rem", color: "var(--color-text-dim)" }}>—</span>;
  }
  return (
    <span style={{
      fontSize: "0.72rem", fontWeight: 700, padding: "0.15rem 0.4rem", borderRadius: 999,
      background: m.delta > 0 ? "var(--color-status-active-bg)" : "var(--color-status-bust-bg)",
      color: m.delta > 0 ? "var(--color-status-active-text)" : "var(--color-status-bust-text)",
    }}>
      {m.delta > 0 ? `▲${m.delta}` : `▼${Math.abs(m.delta)}`}
    </span>
  );
}

// ─── Shared columns (rank, player, score) ────────────────────────────────────

function rankCol(ctx: LeaderboardCtx, t: TFn): LeaderboardColumn {
  return {
    key: "rank",
    header: t("league.leaderboard.rank"),
    width: "44px",
    mobileWidth: "36px",
    renderCell: (m, ctx, isNr) => {
      const rankStr = ctx.ranks.get(m.id) ?? "—";
      const rankNum = parseInt(rankStr, 10);
      const active = ctx.isStarted && !isNr;
      const size = ctx.isMobile ? 22 : 26;
      const style = active ? rankBadgeStyle(rankNum) : { background: "transparent", border: "var(--color-border)", color: "var(--color-text-dim)" };
      return (
        <div
          style={{
            width: size, height: size, borderRadius: "50%", flexShrink: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            background: style.background, border: `1.5px solid ${style.border}`,
          }}
        >
          <span style={{ fontFamily: "var(--font-display)", fontSize: ctx.isMobile ? "0.68rem" : "0.78rem", fontWeight: 700, lineHeight: 1, color: style.color }}>
            {active ? rankStr : "—"}
          </span>
        </div>
      );
    },
  };
}

function playerCol(t: TFn): LeaderboardColumn {
  return {
    key: "player",
    header: t("league.leaderboard.player"),
    width: "1fr",
    mobileWidth: "1fr",
    renderCell: (m, ctx) => {
      const isMe = m.id === ctx.myMemberId;
      return (
        <div style={{ minWidth: 0, display: "flex", alignItems: "center", gap: "0.4rem" }}>
          <Link
            to={memberHref(m, ctx)}
            style={{ fontSize: "0.88rem", fontWeight: isMe ? 600 : 400, color: isMe ? "var(--color-accent)" : "var(--color-text)", textDecoration: "none", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
          >
            {m.displayName}
          </Link>
          {m.userId === null && (
            <span style={{ fontSize: "0.6rem", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--color-text-dim)", flexShrink: 0 }}>
              {t("league.leaderboard.guestBadge")}
            </span>
          )}
        </div>
      );
    },
  };
}

function scoreCol(t: TFn, mobileWidth: string, statusMarker?: (m: Member, ctx: LeaderboardCtx, isNr: boolean) => React.ReactNode): LeaderboardColumn {
  return {
    key: "score",
    header: t("league.leaderboard.score"),
    width: "64px",
    mobileWidth,
    align: "right",
    renderCell: (m, ctx, isNr) => (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "0.25rem" }}>
        {statusMarker?.(m, ctx, isNr)}
        <span style={{ fontFamily: "var(--font-display)", fontSize: ctx.isMobile ? "1rem" : "1.2rem", fontWeight: 700, color: ctx.isStarted && !isNr ? "var(--color-accent)" : "var(--color-text-dim)", textAlign: "right" }}>
          {ctx.isStarted && !isNr ? m.score : "—"}
        </span>
      </div>
    ),
  };
}

function gwDeltaCol(t: TFn): LeaderboardColumn {
  return {
    key: "delta",
    header: t("league.leaderboard.gwDelta"),
    width: "64px",
    mobileWidth: "42px",
    align: "right",
    renderCell: (m, ctx, isNr) => (
      <div style={{ textAlign: "right" }}>{deltaCell(m, isNr, ctx)}</div>
    ),
  };
}

// ─── TARGET columns ───────────────────────────────────────────────────────────

/** Shared state → style lookup for TARGET's targetState — backs both the desktop pill
 * (targetStatusCol) and the mobile score-cell marker (targetStatusMarker), so the two
 * can never drift into inconsistent labels/colors for the same state. */
function targetStatusCfg(t: TFn): Record<"BUST" | "ELIGIBLE" | "INELIGIBLE", { label: string; bg: string; color: string; marker: string; markerLabel: string }> {
  return {
    BUST:       { label: t("league.leaderboard.target.bust"),    bg: "var(--color-status-bust-bg)",   color: "var(--color-status-bust-text)",   marker: "✕", markerLabel: t("league.leaderboard.target.bustMarker") },
    ELIGIBLE:   { label: t("league.leaderboard.target.onTrack"), bg: "var(--color-status-active-bg)", color: "var(--color-status-active-text)", marker: "●", markerLabel: t("league.leaderboard.target.onTrackMarker") },
    INELIGIBLE: { label: t("league.leaderboard.target.waiting"), bg: "color-mix(in srgb, var(--color-warning) 12%, transparent)", color: "var(--color-warning)", marker: "○", markerLabel: t("league.leaderboard.target.waitingMarker") },
  };
}

function targetStatusCol(t: TFn): LeaderboardColumn {
  const cfg = targetStatusCfg(t);
  return {
    key: "status",
    header: t("league.leaderboard.status"),
    width: "80px",
    // hidden on mobile-running
    align: "center",
    renderCell: (m, _ctx, isNr) => {
      if (isNr || !m.targetState) {
        return <span style={{ color: "var(--color-text-dim)", fontSize: "0.7rem" }}>—</span>;
      }
      const c = cfg[m.targetState];
      return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <span style={{ fontSize: "0.6rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em", padding: "2px 6px", borderRadius: 99, background: c.bg, color: c.color, whiteSpace: "nowrap" }}>
            {c.label}
          </span>
        </div>
      );
    },
  };
}

/** Compact single-glyph rendering of the same targetState, shown inside the score cell —
 * the only room left for this on mobile once +GW and the full pill both have to fit on
 * one line. Shape-coded (not color-only) so it survives without relying on hue. */
function targetStatusMarker(t: TFn): (m: Member, ctx: LeaderboardCtx, isNr: boolean) => React.ReactNode {
  const cfg = targetStatusCfg(t);
  return (m, ctx, isNr) => {
    if (isNr || !ctx.isStarted || !ctx.isMobile || !m.targetState) return null;
    const c = cfg[m.targetState];
    return (
      <span aria-label={c.markerLabel} title={c.markerLabel} style={{ fontSize: "0.7rem", fontWeight: 700, color: c.color, lineHeight: 1 }}>
        {c.marker}
      </span>
    );
  };
}

// ─── FINAL_POSITIONS columns ──────────────────────────────────────────────────

function fpBreakdownCols(t: TFn): LeaderboardColumn[] {
  const make = (key: string, header: string, accessor: (bd: NonNullable<Member["fpScoreBreakdown"]>) => number): LeaderboardColumn => ({
    key,
    header,
    width: "46px",
    // hidden on mobile-running
    align: "right",
    renderCell: (m, _ctx, isNr) => {
      const bd = !isNr ? m.fpScoreBreakdown : null;
      const val = bd ? accessor(bd) : undefined;
      return (
        <span style={{ textAlign: "right", fontSize: "0.78rem", color: val ? "var(--color-text)" : "var(--color-text-dim)" }}>
          {bd ? (val ?? 0) : "—"}
        </span>
      );
    },
  });
  return [
    make("fpBase",       t("league.leaderboard.fpBase"),       (bd) => bd.base),
    make("fpBullseye",   t("league.leaderboard.fpBullseye"),   (bd) => bd.bullseye),
    make("fpChampion",   t("league.leaderboard.fpChampion"),   (bd) => bd.champion),
    make("fpTopTier",    t("league.leaderboard.fpTop"),        (bd) => bd.topTier),
    make("fpRelegation", t("league.leaderboard.fpRelegation"), (bd) => bd.relegation),
  ];
}

function fpDeltaCol(t: TFn): LeaderboardColumn {
  return {
    key: "fpDelta",
    header: t("league.leaderboard.fpDelta"),
    width: "50px",
    mobileWidth: "42px",
    align: "right",
    renderCell: (m, ctx, isNr) => (
      <div style={{ textAlign: "right" }}>{deltaCell(m, isNr, ctx)}</div>
    ),
  };
}

// ─── GAMEWEEK_CHALLENGE columns ───────────────────────────────────────────────

function formCol(t: TFn): LeaderboardColumn {
  return {
    key: "form",
    header: t("league.leaderboard.form"),
    width: "72px",
    // hidden on mobile-running
    align: "center",
    renderCell: (m, _ctx, isNr) => {
      const history = m.gwScoreHistory ?? [];
      return (
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          {!isNr && history.length > 0
            ? <FormBar history={history} compact />
            : <span style={{ color: "var(--color-text-dim)", fontSize: "0.7rem" }}>—</span>}
        </div>
      );
    },
  };
}

// ─── getLeaderboardColumns — running/completed state ─────────────────────────

export function getLeaderboardColumns(ctx: LeaderboardCtx, t: TFn): LeaderboardColumn[] {
  const { mode, isStarted, isMobile, showDelta } = ctx;

  switch (mode) {
    case "TARGET":
      return [
        rankCol(ctx, t),
        playerCol(t),
        ...(!isMobile && isStarted ? [targetStatusCol(t)] : []),
        scoreCol(t, "50px", targetStatusMarker(t)),
        ...(showDelta ? [gwDeltaCol(t)] : []),
      ];

    case "FINAL_POSITIONS": {
      const showBreakdown = isStarted && !isMobile;
      return [
        rankCol(ctx, t),
        playerCol(t),
        ...(showBreakdown ? fpBreakdownCols(t) : []),
        scoreCol(t, isStarted ? "50px" : "64px"),
        ...(isStarted ? [fpDeltaCol(t)] : []),
      ];
    }

    case "GAMEWEEK_CHALLENGE":
      return [
        rankCol(ctx, t),
        playerCol(t),
        ...(!isMobile || !isStarted ? [formCol(t)] : []),
        scoreCol(t, "50px"),
        ...(showDelta ? [gwDeltaCol(t)] : []),
      ];

    case "SCORE_PREDICTOR":
      return [
        rankCol(ctx, t),
        playerCol(t),
        scoreCol(t, "50px"),
        ...(showDelta ? [gwDeltaCol(t)] : []),
      ];

    case "SYNDICATE":
      return [
        rankCol(ctx, t),
        playerCol(t),
        scoreCol(t, "50px"),
        ...(showDelta ? [gwDeltaCol(t)] : []),
      ];
  }
}

// ─── getPreparingColumns — PREPARING state ────────────────────────────────────

export type PreparingAction = {
  href: string;
  label: string;
};

export function getPreparingAction(
  mode: LeagueMode,
  isTeamValid: boolean,
  leagueId: string,
  myMemberId: string,
  gwcNextPickMissing: boolean,
  t: TFn,
): PreparingAction {
  switch (mode) {
    case "GAMEWEEK_CHALLENGE":
      return { href: `/leagues/${leagueId}/challenge`, label: gwcNextPickMissing ? t("league.leaderboard.actionSelect") : t("league.leaderboard.actionEdit") };
    case "SCORE_PREDICTOR":
      return { href: `/leagues/${leagueId}/score-predictor`, label: t("league.leaderboard.actionPredict") };
    case "FINAL_POSITIONS":
      return { href: `/leagues/${leagueId}/teams/${myMemberId}`, label: isTeamValid ? t("league.leaderboard.actionEdit") : t("league.leaderboard.actionPredict") };
    case "SYNDICATE":
      return { href: `/leagues/${leagueId}/syndicate`, label: isTeamValid ? t("league.leaderboard.actionEdit") : t("league.leaderboard.actionSelect") };
    default:
      return { href: `/leagues/${leagueId}/teams/${myMemberId}`, label: isTeamValid ? t("league.leaderboard.actionEdit") : t("league.leaderboard.actionSelect") };
  }
}

// Preparing column layouts vary by mode:
// simple (TARGET/FP): "1fr 80px 90px" — Name | Ready | CTA
// GWC/SP: "1fr 90px" — Name | CTA
export type PreparingCols = { gridTemplate: string; showReady: boolean };

export function getPreparingCols(mode: LeagueMode): PreparingCols {
  if (mode === "GAMEWEEK_CHALLENGE" || mode === "SCORE_PREDICTOR") {
    return { gridTemplate: "1fr 90px", showReady: false };
  }
  return { gridTemplate: "1fr 80px 90px", showReady: true };
}
